<template>
  <section class="testimonials-section" id="referenzen">
    <div class="container">
      <div class="section-title text-center">
        <h2>Was unsere <span class="text-gradient">Kunden</span> sagen</h2>
        <p class="lead">Erfahrungsberichte zu unseren KI- und DevOps-Lösungen</p>
      </div>

      <div class="testimonials-carousel" v-if="displayedTestimonials.length > 0">
        <div class="testimonials-container">
          <div 
            v-for="testimonial in displayedTestimonials" 
            :key="testimonial.id" 
            class="testimonial-item">
            <div class="testimonial-content">
              <div class="client-logo">
                <i :class="testimonial.icon"></i>
              </div>
              <p>{{ testimonial.text }}</p>
              <div class="client-info">
                <h4>{{ testimonial.name }}</h4>
                <span>{{ testimonial.position }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="carousel-navigation" v-if="testimonials.length > 4">
          <button class="nav-btn prev" @click="prevSlide" :disabled="currentSlide === 0">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="page-info">
            {{ Math.floor(currentSlide / itemsPerSlide) + 1 }} / {{ Math.ceil(testimonials.length / itemsPerSlide) }}
          </div>
          <button class="nav-btn next" @click="nextSlide" :disabled="currentSlide >= testimonials.length - itemsPerSlide">
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'TestimonialSection',
  data() {
    return {
      currentSlide: 0,
      itemsPerSlide: 4,
      testimonials: [
        {
          id: 1,
          name: "Michael S.",
          position: "CTO, Automobilindustrie",
          text: "Jens Trompler - ITC hat unsere Produktionsplanung durch KI-gestützte Prognosen revolutioniert. Wir konnten unsere Effizienz um 35% steigern.",
          icon: "fas fa-industry"
        },
        {
          id: 2,
          name: "Thomas K.",
          position: "CIO, FinTech",
          text: "Die DevOps-Transformation durch Jens Trompler - ITC hat unsere Release-Zyklen von Monaten auf Tage verkürzt. Ein game-changer für unser Entwicklungsteam.",
          icon: "fas fa-code-branch"
        },
        {
          id: 3,
          name: "Stefan M.",
          position: "CTO, E-Commerce",
          text: "Die Kombination aus KI-Integration und DevOps-Best-Practices hat uns einen entscheidenden Wettbewerbsvorteil verschafft.",
          icon: "fas fa-shopping-cart"
        },
        {
          id: 4,
          name: "Markus R.",
          position: "IT-Direktorin, Energieversorger",
          text: "Durch die KI-gestützte Vorhersage von Systemausfällen und die automatisierte Wiederherstellung konnten wir unsere Systemverfügbarkeit auf 99,9% steigern.",
          icon: "fas fa-bolt"
        },
        {
          id: 5,
          name: "Andreas L.",
          position: "Produktmanager, Logistik",
          text: "Die KI-Integration in unsere Supply-Chain hat den Warenfluss optimiert und Engpässe signifikant reduziert. Eine beeindruckende Lösung!",
          icon: "fas fa-truck"
        },
        {
          id: 6,
          name: "Alexander B.",
          position: "VP Engineering, SaaS",
          text: "Mit dem DevOps-Framework von Jens Trompler - ITC haben wir unsere Infrastrukturkosten um 40% gesenkt und gleichzeitig die Stabilität verbessert.",
          icon: "fas fa-cloud"
        }
      ]
    };
  },
  computed: {
    displayedTestimonials() {
      return this.testimonials.slice(this.currentSlide, this.currentSlide + this.itemsPerSlide);
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      if (window.innerWidth < 768) {
        this.itemsPerSlide = 1;
      } else if (window.innerWidth < 1200) {
        this.itemsPerSlide = 2;
      } else {
        this.itemsPerSlide = 4;
      }
    },
    nextSlide() {
      const maxSlide = this.testimonials.length - this.itemsPerSlide;
      this.currentSlide = Math.min(this.currentSlide + this.itemsPerSlide, maxSlide);
    },
    prevSlide() {
      this.currentSlide = Math.max(this.currentSlide - this.itemsPerSlide, 0);
    },
    goToSlide(index) {
      this.currentSlide = Math.min(index, this.testimonials.length - this.itemsPerSlide);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/global.scss";

.testimonials-section {
  padding: 100px 0;
  position: relative;
  background: linear-gradient(180deg, $background-color, darken($background-color, 3%));
}

.testimonials-carousel {
  margin-top: 50px;
  position: relative;
}

.carousel-navigation {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  padding-top: 20px;
  border-top: 1px solid rgba($accent-color, 0.05);
  
  .nav-btn {
    background: $accent-color;
    color: white;
    border: none;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 10px rgba($accent-color, 0.35);
    
    &:hover {
      background: darken($accent-color, 10%);
      color: white;
      transform: translateY(-3px);
      box-shadow: 0 6px 15px rgba($accent-color, 0.5);
    }
    
    &:disabled {
      background: rgba($accent-color, 0.3);
      color: rgba(white, 0.7);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
    
    i {
      font-size: 1.25rem;
      font-weight: bold;
    }
  }
  
  .page-info {
    margin: 0 40px;
    font-size: 1.1rem;
    color: white;
    background: $accent-color;
    padding: 8px 20px;
    border-radius: 25px;
    font-weight: 600;
    box-shadow: 0 4px 10px rgba($accent-color, 0.25);
  }
}

.testimonials-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
}

.testimonial-item {
  background: rgba($secondary-color, 0.3);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba($accent-color, 0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 5rem;
    font-family: Georgia, serif;
    color: rgba($accent-color, 0.1);
    line-height: 1;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
  
  .testimonial-content {
    position: relative;
    z-index: 1;
    
    .client-logo {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
      
      i {
        font-size: 32px;
        color: $accent-color;
        filter: drop-shadow(0 4px 6px rgba($accent-color, 0.2));
        transition: transform 0.3s ease;
      }
      
      &:hover i {
        transform: scale(1.1);
      }
    }
    
    p {
      font-size: 1.1rem;
      font-style: italic;
      color: $text-color;
      margin-bottom: 20px;
      line-height: 1.6;
    }
    
    .client-info {
      margin-top: 25px;
      padding-top: 15px;
      border-top: 1px solid rgba($accent-color, 0.1);
      
      h4 {
        font-size: 1.1rem;
        color: $accent-color;
        margin: 0;
      }
      
      span {
        font-size: 0.9rem;
        color: $muted-text-color;
      }
    }
  }
}

/* Responsive Styles */
@media (max-width: 768px) {
  .testimonials-container {
    grid-template-columns: 1fr;
  }
}
</style>
